import {format} from 'date-fns';
import {AlertCircle, ImageOff} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {type ItemThumbnailVersion} from '@/api/items';
import {useItemThumbnails} from '@/api/hooks';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {useStoredImageUrl} from '@/components/shared/useStoredImageUrl';

// The preserved bytes, not the source URL: that is the point - the URL can change or die.
function VersionImage({
  version,
  fill
}: {
  readonly version: ItemThumbnailVersion;
  readonly fill?: boolean;
}) {
  const src = useStoredImageUrl(version.sha256);

  if (!src) {
    return (
      <div
        className={`bg-muted text-muted-foreground flex aspect-video shrink-0 items-center justify-center text-xs ${fill ? 'w-full' : 'w-48'}`}>
        Loading…
      </div>
    );
  }

  return (
    <div className={`group relative shrink-0 ${fill ? 'w-full' : 'w-48'}`}>
      <SensitiveMedia
        compact
        src={src}
        alt="Thumbnail version"
        className="aspect-video w-full object-cover"
      />
    </div>
  );
}

// The version that was live when the item was submitted: the latest one first seen on or before then
// (falling back to the earliest we hold, for a submission that predates all of them).
function versionAsSubmitted(versions: ItemThumbnailVersion[], submittedAt: string) {
  const at = new Date(submittedAt).getTime();
  const seen = versions.filter((v) => new Date(v.observed_at).getTime() <= at);
  return seen.length > 0 ? seen.at(-1) : versions[0];
}

export function ThumbnailHistoryDialog({
  itemId,
  open,
  onClose,
  submittedAt
}: {
  readonly itemId: string;
  readonly open: boolean;
  readonly onClose: () => void;
  // When given, the dialog leads with how the item looked when it was submitted versus now.
  readonly submittedAt?: string;
}) {
  const {data, isLoading, isError} = useItemThumbnails(itemId, open);
  const versions = data?.versions ?? [];
  const current = versions.find((v) => v.is_current);
  const asSubmitted = submittedAt ? versionAsSubmitted(versions, submittedAt) : undefined;
  const changedSinceSubmission = asSubmitted && current && asSubmitted.id !== current.id;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Thumbnail history</DialogTitle>
          <DialogDescription>
            Every thumbnail seen for this video, oldest first. Images are preserved copies, so the
            original stays viewable even if the creator replaces it or the video is removed.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div
            className="text-muted-foreground flex items-center justify-center gap-2 p-6 text-xs"
            role="status">
            <Spinner /> Loading…
          </div>
        ) : null}
        {isError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>Could not load thumbnail history.</AlertDescription>
          </Alert>
        ) : null}
        {!isLoading && !isError && versions.length === 0 ? (
          <Empty variant="outline" className="gap-2 p-4">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <ImageOff />
              </EmptyMedia>
              <EmptyTitle>No thumbnail yet</EmptyTitle>
              <EmptyDescription>
                No thumbnail has been captured for this video yet.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : null}
        {asSubmitted && current ? (
          <div className="grid grid-cols-2 gap-3">
            {[
              {label: 'As submitted', version: asSubmitted},
              {label: 'Now', version: current}
            ].map(({label, version}) => (
              <div key={label} className="border-border space-y-1 rounded-lg border p-2">
                <p className="text-sm font-medium">{label}</p>
                <VersionImage fill version={version} />
              </div>
            ))}
            {changedSinceSubmission ? null : (
              <p className="text-muted-foreground col-span-2 text-sm">
                The thumbnail has not changed since this was submitted.
              </p>
            )}
          </div>
        ) : null}
        <ul className="max-h-[50vh] space-y-3 overflow-y-auto">
          {versions.map((version) => (
            <li key={version.id} className="border-border flex gap-3 rounded-md border p-2">
              <VersionImage version={version} />
              <div className="min-w-0 space-y-1 text-sm">
                <div className="flex flex-wrap gap-1">
                  {version.is_original ? <Badge>Original</Badge> : null}
                  {version.is_current ? <Badge variant="outline">Current</Badge> : null}
                </div>
                <p className="text-muted-foreground">
                  First seen {format(new Date(version.observed_at), 'd MMM yyyy HH:mm')}
                </p>
                <p className="text-muted-foreground truncate text-xs" title={version.source_url}>
                  {version.source_url}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
